import { useEffect, useState } from "react";
import TopBar from "@/components/TopBar";
import LandingHero from "@/components/landing/LandingHero";
import LandingMethod from "@/components/landing/LandingMethod";
import LandingTeam from "@/components/landing/LandingTeam";
import LandingCourses from "@/components/landing/LandingCourses";
import LandingPregnancy from "@/components/landing/LandingPregnancy";
import LandingLocations from "@/components/landing/LandingLocations";
import LandingReviews from "@/components/landing/LandingReviews";
import LandingCTA from "@/components/landing/LandingCTA";
import LandingSponsors from "@/components/landing/LandingSponsors";
import StickyWhatsApp from "@/components/landing/StickyWhatsApp";
import { GiftCardPromo } from "@/components/gift/GiftCardPromo";
import { useLandingConfig } from "@/hooks/useLandingConfig";
import api from "@/lib/api";
import { Instagram, Mail, Phone } from "lucide-react";

export default function Landing() {
  // Applica palette + font dinamici dalla config del super_admin
  const { T } = useLandingConfig();
  const footerTagline = T("texts.footer.tagline", "Il tuo spazio di movimento, equilibrio e ritmi naturali. Un metodo costruito su di te.");
  const footerAddress = T("texts.footer.address", "Via Luigi Indelli 91A · Monopoli (BA)");
  const footerEmail = T("texts.footer.email", "epicflowpilates@gmail.com");
  const footerPhone = T("texts.footer.phone", "+39 329 256 1466");
  const footerInstagram = T("texts.footer.instagram", "@epicflowpilates");
  const whatsappUrl = T("cta_links.whatsapp_url", "https://wa.me/393292561466?text=Ciao%20Epic%20Flow!%20Vorrei%20info.");
  const instagramHandleClean = (footerInstagram || "").replace(/^@/, "");

  // v1.5 (2026-09): sedi dinamiche nel footer.
  // Il super_admin le configura da Admin → Sedi (name, address, city, phone,
  // email, instagram_url). Il footer legge la lista pubblica via /api/sedi e
  // la ordina alfabeticamente, così ogni sede è sempre allineata ai dati DB.
  const [sedi, setSedi] = useState([]);
  useEffect(() => {
    api.get("/sedi")
      .then((r) => setSedi(Array.isArray(r.data) ? r.data : []))
      .catch(() => setSedi([]));
  }, []);
  const sortedSedi = [...sedi].sort((a, b) => (a.name || "").localeCompare(b.name || "", "it"));

  return (
    <div data-testid="landing-page" data-landing="true" className="min-h-screen bg-background">
      <TopBar variant="public" />

      <LandingHero />
      <LandingMethod />
      <LandingTeam />
      <LandingCourses />
      <LandingPregnancy />
      <LandingLocations />
      <LandingReviews />

      <GiftCardPromo />
      <LandingCTA />
      <LandingSponsors />
      <StickyWhatsApp />

      {/* Footer minimal */}
      <footer data-testid="landing-footer" className="bg-foreground text-background">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-24">
          <div className="grid md:grid-cols-12 gap-10">
            <div className="md:col-span-5">
              <div className="font-serif-display text-5xl italic tracking-tight">epic flow</div>
              <div className="text-[10px] uppercase tracking-[0.42em] text-background/60 mt-3">Pilates Reformer Studio</div>
              <p className="mt-8 text-sm text-background/70 leading-relaxed max-w-sm font-light">
                {footerTagline}
              </p>
            </div>
            <div className="md:col-span-4">
              <div className="text-[10px] uppercase tracking-[0.42em] text-background/50 mb-4">Contatti</div>
              <ul className="space-y-3 text-sm text-background/85">
                <li className="flex items-center gap-3">
                  <Phone className="w-4 h-4" strokeWidth={1.5}/>
                  <a href={whatsappUrl} target="_blank" rel="noreferrer" className="hover:text-mauve transition-colors">{footerPhone}</a>
                </li>
                <li className="flex items-center gap-3">
                  <Mail className="w-4 h-4" strokeWidth={1.5}/>
                  <a href={`mailto:${footerEmail}`} className="hover:text-mauve transition-colors">{footerEmail}</a>
                </li>
                <li className="flex items-center gap-3">
                  <Instagram className="w-4 h-4" strokeWidth={1.5}/>
                  <a href={`https://instagram.com/${instagramHandleClean}`} target="_blank" rel="noreferrer" className="hover:text-mauve transition-colors">{footerInstagram}</a>
                </li>
              </ul>
              {footerAddress && (
                <div className="mt-4 text-xs text-background/60 leading-relaxed">
                  {footerAddress}
                </div>
              )}
            </div>
            <div className="md:col-span-3">
              <div className="text-[10px] uppercase tracking-[0.42em] text-background/50 mb-4">Sedi</div>
              <ul className="space-y-4 text-sm text-background/85" data-testid="footer-sedi-list">
                {sortedSedi.length === 0 && (
                  <li className="text-background/60 text-xs italic">Prossimamente…</li>
                )}
                {sortedSedi.map((s) => {
                  const igHandle = (s.instagram_url || "").replace(/^@/, "").replace(/^https?:\/\/(www\.)?instagram\.com\//i, "").replace(/\/$/, "");
                  return (
                    <li key={s.id} data-testid={`footer-sede-${s.id}`}>
                      <div className="font-serif-display text-xl text-background">{s.name}</div>
                      {s.address && (
                        <div className="text-background/60 text-xs mt-0.5">{s.address}</div>
                      )}
                      {s.city && (
                        <div className="text-background/60 text-xs">{s.city}</div>
                      )}
                      {(s.phone || s.email || igHandle) && (
                        <div className="mt-1.5 space-y-0.5 text-xs">
                          {s.phone && (
                            <a href={`tel:${(s.phone || "").replace(/\s+/g, "")}`}
                              className="block text-background/70 hover:text-mauve transition-colors">
                              {s.phone}
                            </a>
                          )}
                          {s.email && (
                            <a href={`mailto:${s.email}`}
                              className="block text-background/70 hover:text-mauve transition-colors break-all">
                              {s.email}
                            </a>
                          )}
                          {igHandle && (
                            <a href={`https://instagram.com/${igHandle}`} target="_blank" rel="noreferrer"
                              className="block text-background/70 hover:text-mauve transition-colors">
                              @{igHandle}
                            </a>
                          )}
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>

          <div className="mt-16 pt-8 border-t border-background/15 flex flex-col md:flex-row md:items-center md:justify-between gap-4 text-[10px] uppercase tracking-[0.32em] text-background/50">
            <div>© {new Date().getFullYear()} Epic Flow Pilates. Tutti i diritti riservati.</div>
            <div className="flex items-center gap-6 flex-wrap">
              <a href="/legal/privacy" className="hover:text-background transition-colors">Privacy</a>
              <a href="/legal/cookie" className="hover:text-background transition-colors">Cookie</a>
              <a href="/legal/gdpr" className="hover:text-background transition-colors">GDPR</a>
              <a href="/legal/termini" className="hover:text-background transition-colors">Termini</a>
              <a href="/franchising" className="hover:text-background transition-colors">Franchising</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
